import { useEffect, useRef, useState } from "react";
import { Check, Link2, Loader2 } from "lucide-react";
import { listServers, type ProjectSelection, type Server } from "@/lib/api";
import type { Connector } from "@/lib/connectorCatalog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ConnectorCatalog } from "@/components/ConnectorCatalog";
import { ConnectDialog } from "@/views/Servers";
import { IntegrationLogo } from "@/components/IntegrationLogo";

// AddIntegrationDialog is an in-page "modal version of the Integrations page" for
// the playbook author: attach an integration you already connected, or connect a
// new one (catalog or by URL). Attach delegates to the author's verified
// onAttach; OAuth connects run in a popup so we never leave the author / lose the
// draft. onChanged refreshes the author's server+selection state.
export function AddIntegrationDialog({
  servers,
  selection,
  attaching,
  onAttach,
  onChanged,
  onClose,
}: {
  servers: Server[];
  selection: ProjectSelection | null;
  attaching: string | null;
  onAttach: (serverName: string) => void;
  onChanged: () => Promise<void> | void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [connect, setConnect] = useState<{ prefill: Connector | null } | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null); // OAuth popup in flight
  const pollRef = useRef<number | null>(null);
  const popupRef = useRef<Window | null>(null);
  const aliveRef = useRef(true);
  useEffect(
    () => () => {
      aliveRef.current = false;
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    },
    [],
  );
  const clearPoll = () => {
    if (pollRef.current !== null) window.clearInterval(pollRef.current);
    pollRef.current = null;
    popupRef.current = null;
  };

  const connected = servers
    .filter((s) => s.connected)
    .sort((a, b) => a.name.localeCompare(b.name));
  const isAttached = (name: string) => selection == null || selection.servers.some((s) => s.name === name);

  const afterConnect = async (serverName: string) => {
    setConnect(null);
    await onChanged();
    onAttach(serverName); // connected for this agent → attach it
  };

  // The popup was opened synchronously by ConnectDialog (browsers block a popup
  // opened after the connect await) and is already navigated to the consent URL.
  // Poll until the server reports connected, then close the popup and attach.
  const openOAuth = (_authorizeUrl: string, serverName: string, popup: Window | null) => {
    setConnect(null);
    if (!popup) {
      toast.error("Allow popups to finish connecting, or connect from the Integrations page.");
      return;
    }
    popupRef.current = popup;
    setConnecting(serverName);
    const start = Date.now();
    pollRef.current = window.setInterval(async () => {
      let ok = false;
      try {
        ok = (await listServers()).some((s) => s.name === serverName && s.connected);
      } catch {
        /* transient — keep polling */
      }
      if (!aliveRef.current) return; // unmounted mid-poll; cleanup already ran
      if (ok) {
        clearPoll();
        if (!popup.closed) popup.close();
        setConnecting(null);
        await afterConnect(serverName);
      } else if (popup.closed || Date.now() - start > 180_000) {
        clearPoll();
        setConnecting(null);
        await onChanged();
      }
    }, 2000);
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-h-[85vh] max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add an integration</DialogTitle>
            <DialogDescription>Attach one you've already connected, or connect a new one.</DialogDescription>
          </DialogHeader>

          {connecting && (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Waiting for {connecting} sign-in…
            </div>
          )}

          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your integrations</h4>
            {connected.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing connected yet — connect one below.</p>
            ) : (
              <div className="space-y-1.5">
                {connected.map((s) => (
                  <div key={s.name} className="flex items-center gap-2.5 rounded-lg border px-3 py-2">
                    <IntegrationLogo name={s.name} />
                    <span className="min-w-0 flex-1 truncate text-sm">{s.name}</span>
                    {isAttached(s.name) ? (
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-success">
                        <Check className="h-3.5 w-3.5" /> Added
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 shrink-0" disabled={attaching !== null} onClick={() => onAttach(s.name)}>
                        {attaching === s.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Attach
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Hidden while an OAuth sign-in is in flight, so a second connect
              can't start a competing popup/poll. */}
          {!connecting && (
            <section className="space-y-2.5">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connect a new one</h4>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setConnect({ prefill: null })}>
                  <Link2 className="h-3.5 w-3.5" /> Connect by URL
                </Button>
              </div>
              <ConnectorCatalog compact onPick={(c) => setConnect({ prefill: c })} />
            </section>
          )}
        </DialogContent>
      </Dialog>

      {connect && !connecting && (
        <Dialog open onOpenChange={(o) => !o && setConnect(null)}>
          <ConnectDialog
            reconnect={null}
            prefill={connect.prefill}
            onOAuth={openOAuth}
            onConnected={(name) => void afterConnect(name)}
            onError={(m) => toast.error(m)}
          />
        </Dialog>
      )}
    </>
  );
}
